import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import {
  Users, Crown, Shield, BookOpen, ExternalLink,
  Link as LinkIcon, Ban, UserX, UserPlus, ShieldOff, Check, X, Inbox
} from 'lucide-react';
import { API } from './shared';
import MemberBadgesInline from './BadgeComponents';

const CommunitySidebar = ({
  community, admin, allMembers, membersTotal, onlineCount,
  isAdmin, isCreator, currentUserId, communityId, navigate,
  onKick, onBan, onMembersChange
}) => {
  const [bannedList, setBannedList] = useState([]);
  const [showBanned, setShowBanned] = useState(false);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const loadBanned = async () => {
    try {
      const response = await axios.get(`${API}/communities/${communityId}/banned`, { withCredentials: true });
      setBannedList(response.data.banned || []);
    } catch (error) { console.error(error); }
  };

  const loadJoinRequests = async () => {
    if (!isAdmin) return;
    setLoadingRequests(true);
    try {
      const r = await axios.get(`${API}/communities/${communityId}/join-requests`, { withCredentials: true });
      setJoinRequests(r.data.requests || []);
    } catch (e) {
      // silent — endpoint requires admin and may 403 if not creator+admin race
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    if (isAdmin && community?.join_mode === 'invitation') {
      loadJoinRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, communityId, community?.join_mode]);

  const respondToRequest = async (requestId, accept) => {
    try {
      await axios.post(
        `${API}/communities/${communityId}/join-requests/${requestId}/respond`,
        { accept },
        { withCredentials: true }
      );
      setJoinRequests(prev => prev.filter(r => r.request_id !== requestId));
      if (accept && onMembersChange) onMembersChange();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action impossible');
    }
  };

  const promote = async (userId) => {
    try {
      await axios.post(`${API}/communities/${communityId}/admins`, { user_id: userId }, { withCredentials: true });
      if (onMembersChange) onMembersChange();
    } catch (e) {
      alert(e.response?.data?.detail || 'Impossible de promouvoir');
    }
  };

  const demote = async (userId) => {
    if (!window.confirm('Rétrograder cet admin en membre standard ?')) return;
    try {
      await axios.delete(`${API}/communities/${communityId}/admins/${userId}`, { withCredentials: true });
      if (onMembersChange) onMembersChange();
    } catch (e) {
      alert(e.response?.data?.detail || 'Impossible de rétrograder');
    }
  };

  const handleUnban = async (targetUserId) => {
    try {
      await axios.post(`${API}/communities/${communityId}/unban`, { user_id: targetUserId }, { withCredentials: true });
      setBannedList(prev => prev.filter(b => b.user_id !== targetUserId));
    } catch (error) { alert(error.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-5">
      {/* Pending join requests — Sprint 1.0c V1.a (admin-only) */}
      {isAdmin && community?.join_mode === 'invitation' && (
        <div className="bg-white rounded-2xl shadow-lg p-5" data-testid="community-join-requests">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Inbox className="h-4 w-4 text-amber-500" />
            Demandes d'adhésion ({joinRequests.length})
          </h3>
          {loadingRequests ? (
            <p className="text-sm text-gray-400">Chargement…</p>
          ) : joinRequests.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Aucune demande en attente.</p>
          ) : (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {joinRequests.map(req => (
                <div key={req.request_id} data-testid={`join-request-${req.request_id}`} className="flex items-center gap-2.5 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                  <Avatar className="h-9 w-9 cursor-pointer" onClick={() => navigate(`/app/profile/${req.user_id}`)}>
                    <AvatarImage src={req.avatar} />
                    <AvatarFallback className="bg-gradient-to-br from-amber-400 to-orange-500 text-white text-xs">
                      {(req.display_name || '?').substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{req.display_name || 'Utilisateur'}</p>
                    <p className="text-xs text-gray-400">{req.created_at ? new Date(req.created_at).toLocaleDateString('fr-CH') : ''}</p>
                  </div>
                  <button
                    data-testid={`join-accept-${req.request_id}`}
                    onClick={() => respondToRequest(req.request_id, true)}
                    className="p-1.5 text-emerald-600 hover:bg-emerald-100 rounded"
                    title="Accepter"
                  ><Check className="h-4 w-4" /></button>
                  <button
                    data-testid={`join-reject-${req.request_id}`}
                    onClick={() => respondToRequest(req.request_id, false)}
                    className="p-1.5 text-red-500 hover:bg-red-100 rounded"
                    title="Refuser"
                  ><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Admin Card */}
      {admin && (
        <div className="bg-white rounded-2xl shadow-lg p-5" data-testid="community-admin-card">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2"><Crown className="h-4 w-4 text-yellow-500" />Administrateur</h3>
          <div className="flex items-center gap-3 cursor-pointer hover:bg-purple-50 p-2 rounded-xl -mx-2 transition-colors" onClick={() => navigate(`/app/profile/${admin.user_id}`)}>
            <div className="relative">
              <Avatar className="h-12 w-12"><AvatarImage src={admin.avatar} /><AvatarFallback className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white font-bold">{admin.display_name?.[0]}</AvatarFallback></Avatar>
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">{admin.display_name}</p>
              <p className="text-xs text-purple-600 flex items-center gap-1"><Shield className="h-3 w-3" />Créateur</p>
            </div>
          </div>
          <MemberBadgesInline communityId={communityId} userId={admin.user_id} />
        </div>
      )}

      {/* Members */}
      <div className="bg-white rounded-2xl shadow-lg p-5" data-testid="community-members-sidebar">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2"><Users className="h-4 w-4 text-purple-500" />Membres ({membersTotal})</h3>
          <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">{onlineCount} en ligne</span>
        </div>
        <div className="space-y-1 max-h-[280px] overflow-y-auto">
          {allMembers.slice(0, 15).map(member => (
            <div key={member.user_id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-gray-50 group/member transition-colors" data-testid={`sidebar-member-${member.user_id}`}>
              <div className="relative flex-shrink-0 cursor-pointer" onClick={() => navigate(`/app/profile/${member.user_id}`)}>
                <Avatar className="h-8 w-8"><AvatarImage src={member.avatar} /><AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs">{member.display_name?.substring(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                {member.is_online && <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>}
              </div>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/app/profile/${member.user_id}`)}>
                <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1">
                  {member.display_name}
                  {member.role === 'admin' && (
                    <Shield className="h-3 w-3 text-purple-500" />
                  )}
                </p>
                <p className="text-xs text-gray-400">{member.role === 'admin' ? 'Admin' : member.expertise || 'Membre'}</p>
              </div>
              {isAdmin && member.user_id !== currentUserId && (
                <div className="flex gap-1 opacity-0 group-hover/member:opacity-100 transition-opacity">
                  {/* Creator-only role management */}
                  {isCreator && member.role !== 'admin' && (
                    <button
                      data-testid={`promote-${member.user_id}`}
                      onClick={() => promote(member.user_id)}
                      className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                      title="Promouvoir admin"
                    ><UserPlus className="h-3.5 w-3.5" /></button>
                  )}
                  {isCreator && member.role === 'admin' && member.user_id !== community.creator_id && (
                    <button
                      data-testid={`demote-${member.user_id}`}
                      onClick={() => demote(member.user_id)}
                      className="p-1 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                      title="Rétrograder en membre"
                    ><ShieldOff className="h-3.5 w-3.5" /></button>
                  )}
                  {member.role !== 'admin' && (
                    <>
                      <button onClick={() => onKick(member.user_id)} className="p-1 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded" title="Retirer"><UserX className="h-3.5 w-3.5" /></button>
                      <button onClick={() => onBan(member.user_id)} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" title="Bannir"><Ban className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        {membersTotal > 15 && <button className="w-full mt-3 text-sm text-purple-600 hover:text-purple-700 font-medium">Voir tous les {membersTotal} membres</button>}
        {isAdmin && (
          <button onClick={() => { loadBanned(); setShowBanned(!showBanned); }} className="w-full mt-2 text-xs text-red-500 hover:text-red-600 font-medium flex items-center justify-center gap-1" data-testid="show-banned-btn"><Ban className="h-3 w-3" />Membres bannis</button>
        )}
        {showBanned && bannedList.length > 0 && (
          <div className="mt-3 pt-3 border-t border-red-100 space-y-2">
            <p className="text-xs font-medium text-red-500 uppercase">Bannis</p>
            {bannedList.map(b => (
              <div key={b.user_id} className="flex items-center gap-2 p-2 bg-red-50 rounded-lg">
                <Avatar className="h-7 w-7"><AvatarImage src={b.avatar} /><AvatarFallback className="bg-red-200 text-red-700 text-xs">{b.display_name?.[0]}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0"><p className="text-xs font-medium text-gray-700 truncate">{b.display_name}</p>{b.reason && <p className="text-xs text-gray-400 truncate">{b.reason}</p>}</div>
                <button onClick={() => handleUnban(b.user_id)} className="text-xs text-green-600 hover:text-green-700 font-medium" data-testid={`unban-${b.user_id}`}>Debannir</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rules */}
      <div className="bg-white rounded-2xl shadow-lg p-5" data-testid="community-rules">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2"><BookOpen className="h-4 w-4 text-blue-500" />Regles</h3>
        {(community.rules && community.rules.length > 0) ? (
          <ol className="space-y-2">
            {community.rules.map((rule, i) => (
              <li key={i} className="flex gap-2 text-sm text-gray-700">
                <span className="flex-shrink-0 w-5 h-5 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                <span>{rule}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-gray-400 italic">{isAdmin ? "Ajoutez des regles via 'Gerer'" : "Aucune regle definie"}</p>
        )}
      </div>

      {/* Links */}
      <div className="bg-white rounded-2xl shadow-lg p-5" data-testid="community-links">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2"><LinkIcon className="h-4 w-4 text-cyan-500" />Liens</h3>
        {(community.links && community.links.length > 0) ? (
          <div className="space-y-2">
            {community.links.map((link, i) => (
              <a key={i} href={link.url?.startsWith('http') ? link.url : `https://${link.url}`} target={link.url?.startsWith('/') ? '_self' : '_blank'} rel="noopener noreferrer" className="flex items-center gap-2 p-2.5 rounded-lg bg-gray-50 hover:bg-purple-50 transition-colors group">
                {link.url?.startsWith('/') ? <LinkIcon className="h-4 w-4 text-purple-500" /> : <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-purple-500" />}
                <span className="text-sm font-medium text-gray-700 group-hover:text-purple-600 truncate">{link.title || link.url}</span>
              </a>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">{isAdmin ? "Ajoutez des liens via 'Gerer'" : "Aucun lien"}</p>
        )}
      </div>
    </div>
  );
};

export default CommunitySidebar;
